import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Users, Car, History, FileText, ClipboardList, Building2 } from "lucide-react";
import { api, formatApiError, photoUrl } from "../api";
import { AdminStats, AdminVehiclesTable, AdminUsersTable, AdminQueriesTable, AdminVehicleFormModal, AdminProviders, AdminRequests, AdminFeedback, AdminVisitorStats, AdminBusinesses, AdminQuoteStats } from "../components/AdminPanels";

const EMPTY_VEHICLE = { vin: "", plate: "", brand: "", model: "", year: new Date().getFullYear(), km: 0, engine: "", fuel: "", transmission: "", color: "" };

const VERIFICATION_TAB_ENABLED = true; // kullanici onayiyla canliya alindi (2026-06-25)

const TABS = [
  { key: "ozet", label: "Özet", Icon: LayoutDashboard },
  { key: "araclar", label: "Araç Kayıtları", Icon: Car },
  { key: "bireysel", label: "Bireysel Kullanıcılar", Icon: Users },
  { key: "kurumsal", label: "Kurumsal Kullanıcılar", Icon: Users },
  { key: "isletmeler", label: "İşletmeler", Icon: Building2 },
  { key: "sorgular", label: "Sorgular", Icon: History },
  { key: "talepler", label: "Hizmet Talepleri", Icon: FileText },
  ...(VERIFICATION_TAB_ENABLED ? [{ key: "dogrulama", label: "Ruhsat Doğrulama", Icon: ClipboardList }] : []),
];

export default function Admin() {
  const [tab, setTab] = useState("ozet");
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [users, setUsers] = useState(null);
  const [queries, setQueries] = useState(null);
  const [vehicleForm, setVehicleForm] = useState(null);
  const [providers, setProviders] = useState(null);
  const [talepler, setTalepler] = useState(null);
  const [degDurum, setDegDurum] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [verifs, setVerifs] = useState(null);
  const [viewRuhsat, setViewRuhsat] = useState(null);
  const [ruhsatZoom, setRuhsatZoom] = useState(false);
  const [ruhsatImgErr, setRuhsatImgErr] = useState(false);
  const [visitorStats, setVisitorStats] = useState(null);
  const [quoteStats, setQuoteStats] = useState(null);
  const [qperiod, setQperiod] = useState("ay");

  const loadQuoteStats = useCallback((p) => {
    api.get(`/admin/teklif-istatistik?period=${p}`).then((r) => setQuoteStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const loadTab = useCallback((t) => {
    if (t === "ozet") {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
      api.get("/admin/degerleme-durum").then((r) => setDegDurum(r.data)).catch(() => setDegDurum(null));
      api.get("/admin/geri-bildirim").then((r) => setFeedback(r.data)).catch((e) => toast.error(formatApiError(e)));
      api.get("/admin/istatistikler").then((r) => setVisitorStats(r.data)).catch((e) => toast.error(formatApiError(e)));
    }
    if (t === "araclar") api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "bireysel" || t === "kurumsal") api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "sorgular") api.get("/admin/queries").then((r) => setQueries(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "talepler") api.get("/admin/talepler").then((r) => setTalepler(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "isletmeler") api.get("/admin/providers").then((r) => setProviders(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (t === "dogrulama") api.get("/admin/verifications").then((r) => setVerifs(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const verifAksiyon = (id, aksiyon) => api.post(`/admin/verifications/${id}/aksiyon`, { aksiyon })
    .then(() => { setViewRuhsat(null); loadTab("dogrulama"); })
    .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);
  useEffect(() => { if (tab === "talepler") loadQuoteStats(qperiod); }, [tab, qperiod, loadQuoteStats]);

  const submitVehicle = async (e) => {
    e.preventDefault();
    const d = vehicleForm.data;
    const payload = { ...d, year: Number(d.year), km: Number(d.km) || 0 };
    try {
      if (vehicleForm.mode === "add") {
        await api.post("/admin/vehicles", payload);
        toast.success("Araç kaydı eklendi");
      } else {
        await api.put(`/admin/vehicles/${vehicleForm.id}`, payload);
        toast.success("Araç kaydı güncellendi");
      }
      setVehicleForm(null);
      loadTab("araclar");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteVehicle = async (v) => {    if (!window.confirm(`${v.vin} kaydı silinsin mi?`)) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      toast.success("Araç silindi");
      loadTab("araclar");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısı ve araçları silinsin mi?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      loadTab("bireysel");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteQuery = async (q) => {
    try {
      await api.delete(`/admin/queries/${q.id}`);
      toast.success("Sorgu kaydı silindi");
      loadTab("sorgular");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (v) => setVehicleForm({ mode: "edit", id: v.id, data: { vin: v.vin, plate: v.plate, brand: v.brand, model: v.model, year: v.year, km: v.km, engine: v.engine, fuel: v.fuel, transmission: v.transmission, color: v.color } });

  const feedbackStatus = async (fid, status) => {
    try {
      await api.put(`/admin/geri-bildirim/${fid}/durum`, { status });
      toast.success("Geri bildirim durumu güncellendi");
      api.get("/admin/geri-bildirim").then((r) => setFeedback(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const providerAction = async (kind, uid, action) => {
    try {
      await api.post(`/admin/providers/${kind}/${uid}/durum`, { status: action });
      toast.success("Sağlayıcı durumu güncellendi");
      api.get("/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-6 h-6 text-amber-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Yönetim Paneli</h1>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-px">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-xl border-b-2 transition-colors ${
                tab === t.key ? "border-cyan-400 text-cyan-300 bg-cyan-400/5" : "border-transparent text-slate-400 hover:text-slate-200"
              }`}>
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {VERIFICATION_TAB_ENABLED && tab === "dogrulama" && (
            <div data-testid="admin-dogrulama" className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
              <h2 className="text-lg font-semibold text-slate-100 mb-4">Ruhsat / Şasi Doğrulama Kuyruğu</h2>
              {!verifs ? <p className="text-sm text-slate-500">Yükleniyor...</p> : verifs.length === 0 ? (
                <p className="text-sm text-slate-500">Doğrulama bekleyen araç yok.</p>
              ) : (
                <div className="space-y-3">
                  {verifs.map((x) => (
                    <div key={x.id} data-testid={`verif-${x.id}`} className="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-white/10 bg-slate-950/50">
                      <div className="flex-1 min-w-48">
                        <p className="text-sm font-bold text-slate-100">{x.brand} {x.model} {x.year}</p>
                        <p className="text-xs font-mono-vin text-cyan-300 mt-0.5">{x.vin}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{x.owner_email}</p>
                      </div>
                      <span className="text-xs px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-300 font-semibold">
                        {({ dogrulandi: "Doğrulandı", inceleme_bekliyor: "İnceleme Bekliyor", ruhsat_okunamadi: "Ruhsat Okunamadı", eslesmedi: "Ruhsat/Şasi Eşleşmedi", reddedildi: "Reddedildi" })[x.verification?.status] || x.verification?.status}
                      </span>
                      <div className="flex gap-2">
                        <button data-testid={`verif-onayla-${x.id}`} onClick={() => api.post(`/admin/verifications/${x.id}/aksiyon`, { aksiyon: "onayla" }).then(() => loadTab("dogrulama")).catch((e) => toast.error(formatApiError(e)))}
                          className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/25">Onayla</button>
                        <button data-testid={`verif-reddet-${x.id}`} onClick={() => api.post(`/admin/verifications/${x.id}/aksiyon`, { aksiyon: "reddet" }).then(() => loadTab("dogrulama")).catch((e) => toast.error(formatApiError(e)))}
                          className="px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-400/40 text-red-300 text-xs font-bold hover:bg-red-500/25">Reddet</button>
                        <button data-testid={`verif-incele-${x.id}`} onClick={() => { setViewRuhsat(x); setRuhsatZoom(false); setRuhsatImgErr(false); }}
                          className="px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-500/25">İncele</button>
                        <button data-testid={`verif-sasi-${x.id}`} onClick={() => { const nv = window.prompt("Doğru şasi/VIN numarası:", x.vin); if (nv) api.post(`/admin/verifications/${x.id}/aksiyon`, { aksiyon: "sasi_duzelt", yeni_vin: nv }).then(() => loadTab("dogrulama")).catch((e) => toast.error(formatApiError(e))); }}
                          className="px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-500/25">Şasi Düzelt</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === "ozet" && (
            <>
              <AdminStats stats={stats} />
              <div data-testid="admin-degerleme-durum" className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5">
                <h3 className="text-sm font-semibold text-slate-300 mb-3">Araç Değerleme Veri Kaynağı</h3>
                {degDurum === null ? <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /> : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                    <div><p className="text-slate-500">Veri Kaynağı</p>
                      <p data-testid="deg-kaynak" className="text-slate-100 font-semibold mt-1">{degDurum.kaynak || "Bağlı değil"}</p></div>
                    <div><p className="text-slate-500">Durum</p>
                      <p data-testid="deg-durum" className={`font-semibold mt-1 ${degDurum.bagli ? "text-emerald-300" : "text-amber-300"}`}>
                        {degDurum.bagli ? "Bağlı" : "API bekleniyor"}</p></div>
                    <div><p className="text-slate-500">Toplam Sorgu</p>
                      <p className="text-slate-100 font-semibold mt-1">{degDurum.toplam_sorgu}</p></div>
                    <div><p className="text-slate-500">Son Veri Güncelleme</p>
                      <p data-testid="deg-guncelleme" className="text-slate-100 font-semibold mt-1">{degDurum.son_veri_guncelleme ? new Date(degDurum.son_veri_guncelleme).toLocaleString("tr-TR") : "—"}</p></div>
                    <div><p className="text-slate-500">Son Ortalama Fiyat</p>
                      <p data-testid="deg-ortalama" className="text-slate-100 font-semibold mt-1">{degDurum.son_ortalama_fiyat != null ? `${Number(degDurum.son_ortalama_fiyat).toLocaleString("tr-TR")} ₺` : "—"}</p></div>
                    <div><p className="text-slate-500">Son Hata / API</p>
                      <p data-testid="deg-hata" className={`font-semibold mt-1 ${degDurum.son_hata ? "text-red-300" : "text-emerald-300"}`}>
                        {degDurum.son_hata ? `${degDurum.son_hata.mesaj?.slice(0, 60)}` : "Yok"}</p></div>
                  </div>
                )}
              </div>
              {feedback !== null && (
                <div className="mt-10">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3">Geri Bildirimler</h3>
                  <AdminFeedback items={feedback} onStatus={feedbackStatus} />
                </div>
              )}
              {visitorStats !== null && (
                <div className="mt-10">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3">Ziyaretçi İstatistikleri</h3>
                  <AdminVisitorStats stats={visitorStats} />
                </div>
              )}
            </>
          )}
          {tab === "araclar" && (
            <AdminVehiclesTable vehicles={vehicles}
              onAdd={() => setVehicleForm({ mode: "add", data: { ...EMPTY_VEHICLE } })}
              onEdit={openEdit} onDelete={deleteVehicle} />
          )}
          {tab === "bireysel" && (
            <AdminUsersTable users={users === null ? null : users.filter((u) => u.role === "user" && (u.membership_type || "bireysel") === "bireysel")} onDelete={deleteUser} />
          )}
          {tab === "kurumsal" && (
            <AdminUsersTable users={users === null ? null : users.filter((u) => u.role === "user" && u.membership_type === "kurumsal")} onDelete={deleteUser} />
          )}
          {tab === "isletmeler" && <AdminBusinesses providers={providers} onAction={providerAction} />}
          {tab === "sorgular" && <AdminQueriesTable queries={queries} onDelete={deleteQuery} />}
          {tab === "talepler" && (
            <div className="space-y-8">
              <AdminQuoteStats stats={quoteStats} period={qperiod} onPeriod={setQperiod} />
              <AdminRequests data={talepler} />
            </div>
          )}
        </div>

        {vehicleForm && (
          <AdminVehicleFormModal vehicleForm={vehicleForm} setVehicleForm={setVehicleForm}
            onSubmit={submitVehicle} onClose={() => setVehicleForm(null)} />
        )}

        {viewRuhsat && (
          <div data-testid="ruhsat-viewer-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => { setViewRuhsat(null); setRuhsatZoom(false); setRuhsatImgErr(false); }}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-slate-900 p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-bold text-slate-100">{viewRuhsat.brand} {viewRuhsat.model} {viewRuhsat.year}</p>
                  <p data-testid="ruhsat-viewer-vin" className="text-xs font-mono-vin text-cyan-300 mt-0.5">{viewRuhsat.vin}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{viewRuhsat.owner_email}</p>
                </div>
                <button data-testid="ruhsat-viewer-close" onClick={() => { setViewRuhsat(null); setRuhsatZoom(false); setRuhsatImgErr(false); }}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-slate-300 text-xs font-bold hover:bg-white/10">Kapat</button>
              </div>
              {(() => {
                const periods = viewRuhsat.period_ruhsats || [];
                const src = photoUrl(viewRuhsat.verification?.ruhsat_photo || (periods.length ? periods[periods.length - 1].ruhsat_photo : ""));
                if (!src) return <p data-testid="ruhsat-viewer-empty" className="mt-4 text-sm text-amber-300">Bu kayıt için yüklenmiş ruhsat görseli bulunamadı.</p>;
                return (
                  <div className="mt-4">
                    {ruhsatImgErr ? (
                      <div data-testid="ruhsat-viewer-error" className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 text-xs">
                        <p className="font-bold text-red-300">Görsel yüklenemedi (dosya bulunamadı veya erişim hatası).</p>
                        <p className="mt-1 break-all text-red-200/70">{src}</p>
                      </div>
                    ) : (
                      <button type="button" data-testid="ruhsat-viewer-zoom-toggle" onClick={() => setRuhsatZoom((z) => !z)} className="block w-full cursor-zoom-in">
                        <img data-testid="ruhsat-viewer-image" src={src} alt="Ruhsat görseli" onError={() => setRuhsatImgErr(true)}
                          className={ruhsatZoom ? "w-full rounded-xl border border-white/10" : "w-full max-h-96 object-contain rounded-xl border border-white/10 bg-slate-950"} />
                      </button>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <a data-testid="ruhsat-viewer-open-tab" href={src} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-500/25">Büyüt / Yeni Sekmede Aç</a>
                      <span className="text-[11px] text-slate-500">Görsele dokunarak yakınlaştırıp uzaklaştırabilirsiniz.</span>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                <button data-testid="ruhsat-viewer-onayla" onClick={() => verifAksiyon(viewRuhsat.id, "onayla")}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/25">Onayla</button>
                <button data-testid="ruhsat-viewer-reddet" onClick={() => verifAksiyon(viewRuhsat.id, "reddet")}
                  className="px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-400/40 text-red-300 text-xs font-bold hover:bg-red-500/25">Reddet</button>
                <button data-testid="ruhsat-viewer-inceleme" onClick={() => verifAksiyon(viewRuhsat.id, "inceleme")}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/15 text-slate-300 text-xs font-bold hover:bg-white/10">İncelemeye Al</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
